<template>
  <div class="text-patch-editor">
    <div class="editor-colors">
      <div class="list-colors">
        <div class="list-colors__header">COLORS</div>

          <ul class="colorpicker">
            <PatchColorItem
              v-for="(colorData, i) in colors"
              :id="`patch-${i}`"
              :key="`patch-${colorData.hex}-${colorData.id}`"
              :color="colorData.hex"
              :colorIdByTextPatch="colorData.id"
              :layer-id="colorData.hex"
              :material="colorData.material"
              :index="i"
              :editableProperty="'fill'"
              @onChangeColor="onChangeColor"
            />
          </ul>
        </div>
      </div>
    <div id="wrap">
      <div
        id="text-svg-container"
        :style="{'padding-top': `${containerPadding}px`}"
      >
      </div>
      <div id="temp-svg-container"></div>
    </div>

    <div class="text-editor">
      <b-form-input
        placeholder="Enter a text here..."
        v-model="text"
        class="text-element control"
      />

      <div class="curve-control control">
        <label>Arch</label>

        <vue-slider
          ref="sizeSlider"
          class="curve-slider"
          v-model="archAngle"
          :min="-180"
          :max="180"
          :interval="1"
        />

        <b-form-input
          placeholder="Enter a text here..."
          v-model="archAngle"
          class="text-element"
        />

        <label>Spacing</label>

        <vue-slider
          ref="sizeSlider"
          class="curve-slider"
          v-model="curveCoeff"
          :min="0.5"
          :max="2"
          :interval="0.01"
          @change="onChangeCurveCoeff"
        />

        <b-form-input
          placeholder="Enter a text here..."
          v-model="curveCoeff"
          class="text-element"
        />

      </div>
    </div>

    <div class="modal-footer border-top-0 p-0 pt-3 mt-3 d-flex justify-content-between">
      <b-button variant="danger" @click="cancel">CANCEL PATCH</b-button>

      <b-button
        :disabled="isSavingPatch"
        variant="success"
        @click="save"
      >
        ADD TO JACKET
      </b-button>
    </div>
  </div>
</template>

<script>
import * as SVG from 'svg.js/dist/svg';
import PatchColorItem from '@/components/canvas/PatchColorItem.vue';
import { uniqueId } from '@/common/utils';
import * as htmlToImage from 'html-to-image';
import 'vue-slider-component/theme/default.css';
import VueSlider from 'vue-slider-component';

export default {
  name: 'TextPatchEditor',

  components: {
    VueSlider,
    PatchColorItem
  },

  props: {
    modalId: String,
    patchId: String,
    patchCategoryId: String,
    size: Object,
    originalPatchId: String,
    isTextEmbroidery: Boolean
  },

  data () {
    let colors = [
      {
        id: 0, order: 1, hex: '#3A75C4', title: 'Columbia Blue', material: 'felt'
      },
      {
        id: 1, order: 2, hex: '#FCD116', title: 'Yellow Gold', material: 'felt'
      },
      {
        id: 2, order: 3, hex: '#080605', title: 'Black', material: 'chenille'
      },
      {
        id: 3, order: 4, hex: '#FFFFFF', title: 'White', material: 'chenille'
      },
    ];

    if (this.isTextEmbroidery) {
      colors = [
        {
          id: 0, order: 1, hex: '#FFFFFF', title: 'White', material: 'chenille'
        }
      ];
    }

    return {
      canvasId: '',
      text: 'New text',
      colors: colors.sort((a, b) => (a.id > b.id ? 1 : -1)),
      activeSide: null,
      isSavingPatch: false,
      archAngle: 0,
      curveCoeff: 1,
      textWidth: 0,
      textHeight: 0,
      schoolColors: null
    };
  },

  computed: {
    patches () {
      return this.$store.getters['design/getProjectPatches'];
    },
    patchesBySide () {
      return (jacketSide) => this.$store.getters['design/getPatchesBySide'](jacketSide);
    },
    jacketSide () {
      return this.$store.getters['ui/getJacketSide'];
    },
    totalPrice () {
      return this.$store.getters['finalizeOrder/getTotalPrice'];
    },

    fontData () {
      if (this.patchId) {
        const patch = this.patchById(this.patchId);
        const category = this.$store.getters['elements/getPatchesCategoryById'](patch.categoryId);
        const fonts = this.$store.getters['elements/getFontsByCategory'](category.name);
        return fonts.find((font) => font.title === patch.name);
      }
      return this.$store.getters['elements/getActiveFont'];
    },

    patchById () {
      return (patchId) => this.$store.getters['design/getPatchById'](patchId);
    },

    containerPadding () {
      const startOffset = 34; // height of colors header
      let angleOffset = 0;
      if (this.archAngle > 0) {
        const groupWrapper = this.canvas.select(`#${this.canvasId}--wrapper`).first();
        const { height } = groupWrapper.node.getBBox();
        angleOffset = height - this.textHeight;
      }
      return startOffset + angleOffset;
    }
  },

  mounted () {
    this.init();
    this.activeSide = this.jacketSide;
  },

  watch: {
    jacketSide () {
      this.activeSide = this.jacketSide;
    },

    archAngle () {
      this.curveText();
    },

    curveCoeff () {
      this.curveText(false, false);
    },

    text () {
      this.clearText();
      this.addText();
    },

    colors () {
      this.clearText();
      this.addText();
    }
  },

  methods: {
    init () {
      this.schoolColors = this.$store.getters['elements/getSchoolColors'];
      this.canvasId = this.patchId ? this.patchId : uniqueId('project-patch');

      if (this.patchId) {
        const patch = this.patchById(this.patchId);
        this.colors = patch.colors;
        this.text = patch.text;
        // eslint-disable-next-line no-nested-ternary
        this.archAngle = patch?.archAngle ? patch.archAngle
          : patch?.angle ? patch?.angle : 0;
        this.curveCoeff = patch?.curveCoeff ? patch?.curveCoeff : 1;
      }

      // if (this.schoolColors) {
      //   this.colors = this.colors.map((color, index) => {
      //     return {
      //       ...color,
      //       hex: Object.values(this.schoolColors)[index]
      //         ? Object.values(this.schoolColors)[index] : this.colors[index].hex
      //     };
      //   });
      // }

      this.canvas = SVG('text-svg-container').size(350, 150);
      this.canvas.id(this.canvasId);
      this.canvas.group().id(`${this.canvasId}--wrapper`);

      setTimeout(() => {
        this.clearText();
        this.addText();
      }, 100);
    },

    cancel () {
      this.$bvModal.hide(this.modalId);
    },

    onChangeColor (newColor, oldColorHex, colorId) {
      this.colors = this.colors.map((color) => {
        if (color.id === colorId) {
          return { ...color, hex: newColor.hex, title: newColor.title };
        }

        this.clearText();
        this.addText();
        return color;
      });
    },

    onChangeCurveCoeff (value) {
      this.curveCoeff = value;
    },

    clearText () {
      const text = this.canvas.select('text').first();
      if (text) {
        const pathId = text.textPath().attr('href');
        const pathElem = this.canvas.select(pathId).get(0);
        pathElem.remove();
        text.remove();
      }
    },

    addText () {
      const groupWrapper = this.canvas.select(`#${this.canvasId}--wrapper`).first();
      let text = null;
      let fontTspanWeight = 3;

      if (this.colors[1]?.hex && this.colors[1].hex === this.colors[2].hex) {
        fontTspanWeight = 0.5;
      }

      if (this.isTextEmbroidery) {
        text = groupWrapper.text((add) => {
          add
            .tspan(this.text)
            .attr('stroke-linejoin', 'round')
            .attr('style', `
              fill: ${this.colors[0].hex};
              stroke-width: 0.2;
              font-family: ${this.fontData.font};
              font-size: 100px;
            `);
        });
      } else {
        text = groupWrapper.text((add) => {
          this.addTspan(add, 0, 20);
          this.addTspan(add, 1, 10);
          this.addTspan(add, 2, fontTspanWeight);
        });
      }

      text.lines().members.forEach((tspan) => {
        tspan.x(this.fontData.xPos);
      });

      text.attr('style', `
       letter-spacing: 1px;
       -webkit-font-smoothing: antialiased;
       text-rendering: optimizeLegibility;
     `);

      this.textWidth = text.bbox().width;
      this.textHeight = text.bbox().height;
      this.curveText(true, false);
    },

    addTspan (add, strokeColorIndex, strokeWidth) {
      let fontWeight;
      if (strokeColorIndex === 2) {
        fontWeight = 'normal';
      } else {
        fontWeight = 'bold';
      }
      add
        .tspan(this.text)
        .attr('stroke-linejoin', 'round')
        .attr('style', `
          fill: ${this.colors[3].hex};
          stroke: ${this.colors[strokeColorIndex].hex};
          stroke-width: ${strokeWidth};
          font-family: ${this.fontData.font};
          font-size: 100px;
          font-weight: ${fontWeight};
        `);
    },

    curveText (isInit = false, isAutoCurve = true) {
      const groupWrapper = this.canvas.select(`#${this.canvasId}--wrapper`).first();
      const text = groupWrapper.select('text').get(0);
      if (!text) return false;

      const yPos = this.fontData.yPosFor100px;
      const angle = parseInt(this.archAngle, 10);

      if (isAutoCurve) {
        this.curveCoeff = angle >= 0 ? 1 : 1.2;

        if (this.fontData.font === 'Freshman' && angle < 0) {
          this.curveCoeff = 1.1;
        }
      }

      const path = `
      M 0 ${yPos}
      C 0 ${yPos}
      ${this.textWidth / 2} ${yPos - angle}
      ${this.textWidth * this.curveCoeff} ${yPos}
      `;

      if (isInit) {
        this.path(text, path);
      } else {
        text.plot(path);
      }

      const pathId = text.textPath().attr('href');
      const pathElem = this.canvas.select(pathId).get(0);
      const pathLength = pathElem.node.getTotalLength();
      const countTspan = text.lines().length();
      text.textPath().attr('textLength', pathLength * countTspan);

      return true;
    },

    path (textElem, d) {
      const path = new SVG.TextPath();

      const track = new SVG.Path();
      track.plot(d);
      track.fill('transparent');
      textElem.after(track);

      while (textElem.node.hasChildNodes()) {
        path.node.appendChild(textElem.node.firstChild);
      }

      textElem.node.appendChild(path.node);

      // eslint-disable-next-line
      path.attr('href', '#' + track, SVG.xlink);
    },

    takePatchScreenshot () {
      const wrapper = this.canvas.select(`#${this.canvas.id()}--wrapper`).first();
      const bbox = wrapper.node.getBBox();
      let x = bbox.x - 10;
      let width = bbox.width + 20;
      let y = bbox.y;
      let height = bbox.height + 10;
      if (this.fontData.font === 'Freshman') {
        x = bbox.x - 10;
        width = this.archAngle === 0 ? bbox.width + 20 : bbox.width + 10;
        y = bbox.y - 12;
        height = bbox.height + 12;
      } else if (this.fontData.font === 'BrisenTail') {
        y = bbox.y - 20;
        height = bbox.height + 30;
      }

      const tempCanvas = this.canvas;
      const tempCanvasId = this.canvasId;
      this.canvas = SVG('temp-svg-container');
      this.canvas.id('temp-svg');
      this.canvas.group().id(`${this.canvasId}--wrapper`);
      this.canvas.width(width).height(height);
      this.canvas.viewbox(x, y, width, height);
      this.clearText();
      this.addText();

      this.canvas.node.style.fontFamily = this.fontData.font;

      return htmlToImage.toPng(this.canvas.node, { pixelRatio: 1 })
        .then((image) => {
          this.canvas = tempCanvas;
          this.canvasId = tempCanvasId;

          // test image for patch
          // const img = document.createElement('img');
          // img.src = image;
          // img.style.backgroundColor = 'aqua';
          // document.getElementsByTagName('body')[0].appendChild(img);
          return image;
        });
    },

    save () {
      this.isSavingPatch = true;
      this.takePatchScreenshot().then((picture) => {
        const wrapper = this.canvas.select(`#${this.canvas.id()}--wrapper`).first();
        const bbox = wrapper.node.getBBox();
        const { width, height } = bbox;
        this.canvas.viewbox(0, 0, width, height);
        this.canvas.width(width);
        this.canvas.height(height);
        const id = this.canvasId;
        const svgText = this.canvas.svg();
        let name;
        let categoryId;
        let patchSize;
        let side;
        if (!this.patchId) {
          name = this.fontData.title;
          categoryId = this.patchCategoryId;
          patchSize = this.size;
          side = this.activeSide;
          this.$store.dispatch('design/setPatchIdBySide', { id, side: this.activeSide });
        } else {
          name = this.patchesBySide(side).data[this.patchId].name;
          categoryId = this.patchesBySide(side).data[this.patchId].categoryId;
          patchSize = this.patchesBySide(side).data[this.patchId].patchSize;
          side = this.patchesBySide(side).data[this.patchId].side;
        }
        const action = this.patchId ? 'design/updatePatch' : 'design/addPatchToProject';
        this.$store.dispatch(action, {
          id,
          originalId: this.originalPatchId,
          svgText,
          name,
          font: this.fontData.font,
          categoryId,
          patchSize,
          side,
          isTextPatch: true,
          colors: this.colors,
          text: this.text,
          archAngle: this.archAngle,
          curveCoeff: this.curveCoeff,
          textHeight: this.textHeight,
          isTextEmbroidery: !!this.isTextEmbroidery,
          screenShot: picture
        });
        this.$store.dispatch('finalizeOrder/calculateTotalPrice');
        setTimeout(() => {
          const eventName = this.patchId ? 'Global_updatePatchInProject' : 'Global_addPatchToProject';

          this.$events.emit(eventName, { patchId: id });
        }, 0);
        this.isSavingPatch = false;
        this.$bvModal.hide(this.modalId);
      });
    }
  }
};
</script>

<style scoped>
  .editor-colors {
    left: 15px;
    position: absolute;
    width: 140px;
  }

  #text-svg-container {
    width: 100%;
    height: 400px;
    margin: auto auto 20px;
    padding-left: 25%;

    svg {
      margin-left: 10px;
    }
  }

  #temp-svg-container {
    width: 400px;
    height: 400px;
    position: absolute;
    left: 10000px;
    top: -1000px;
  }

  .control {
  }

  .curve-control {
    display: flex;
    align-items: center;
    gap: 1rem;

    .curve-slider {
      flex: 1;
      margin: 15px;
    }

    .text-element {
      width: 60px;
    }

    label {
      margin: 0;
    }
  }
</style>
